import Modal from 'react-modal';
import { FiX, FiSend } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    padding: '0',
    border: 'none',
    background: 'transparent',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'visible',
  },
  overlay: {
    backgroundColor: 'rgba(5, 5, 5, 0.6)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    zIndex: 1000,
  },
};

const HireMeModal = ({ isOpen, onRequestClose }) => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      return toast.warning('Please fill in all fields before submitting.', { theme: 'dark' });
    }

    setIsSubmitting(true);
    
    // Simulate network delay for effect
    await new Promise((resolve) => setTimeout(resolve, 1500));
    
    setIsSubmitting(false);
    toast.success('Your project request was sent successfully! I will reach out shortly.', {
      theme: 'dark'
    });
    setFormData({ name: '', email: '', message: '' });
    onRequestClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={!isSubmitting ? onRequestClose : undefined}
      style={customStyles}
      ariaHideApp={false}
      contentLabel="Hire Me Modal"
      closeTimeoutMS={300}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="glass-panel rounded-3xl p-6 md:p-10 relative overflow-hidden shadow-2xl border border-black/5 dark:border-white/10"
      >
        {/* Glow Effects */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-[#0f766e]/20 dark:from-[#D4F06B]/20 to-transparent rounded-full blur-3xl -z-10 transform translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-indigo-500/20 dark:from-[#00F0FF]/20 to-transparent rounded-full blur-3xl -z-10 transform -translate-x-1/2 translate-y-1/2"></div>

        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-3xl font-general-bold text-slate-900 dark:text-white tracking-tight">
              Start a Project
            </h2>
            <p className="text-sm font-general-medium text-slate-500 dark:text-slate-400 mt-2">
              Let's engineer something phenomenal together.
            </p>
          </div>
          <button
            onClick={onRequestClose}
            disabled={isSubmitting}
            className="p-2 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-700 transition-all border border-black/5 dark:border-white/5 disabled:opacity-50"
            aria-label="Close modal"
          >
            <FiX size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label htmlFor="name" className="block text-slate-700 dark:text-slate-300 text-sm font-general-medium mb-1.5 ml-1">
              Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={isSubmitting}
              placeholder="Elon Musk"
              className="w-full bg-slate-50 border border-slate-200 dark:bg-slate-900/50 dark:border-slate-700 rounded-xl px-4 py-3 text-slate-900 dark:text-white focus:ring-2 focus:ring-[#0f766e]/50 dark:focus:ring-[#D4F06B]/50 focus:border-[#0f766e] dark:focus:border-[#D4F06B] outline-none transition-all placeholder-slate-400 dark:placeholder-slate-600 font-general-regular disabled:opacity-50"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-slate-700 dark:text-slate-300 text-sm font-general-medium mb-1.5 ml-1">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              disabled={isSubmitting}
              placeholder="elon@x.com"
              className="w-full bg-slate-50 border border-slate-200 dark:bg-slate-900/50 dark:border-slate-700 rounded-xl px-4 py-3 text-slate-900 dark:text-white focus:ring-2 focus:ring-[#0f766e]/50 dark:focus:ring-[#D4F06B]/50 focus:border-[#0f766e] dark:focus:border-[#D4F06B] outline-none transition-all placeholder-slate-400 dark:placeholder-slate-600 font-general-regular disabled:opacity-50"
            />
          </div>

          <div>
            <label htmlFor="message" className="block text-slate-700 dark:text-slate-300 text-sm font-general-medium mb-1.5 ml-1">
              Project Details
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              disabled={isSubmitting}
              rows="4"
              placeholder="Tell me about your architecture scale, budget, and timeline..."
              className="w-full bg-slate-50 border border-slate-200 dark:bg-slate-900/50 dark:border-slate-700 rounded-xl px-4 py-3 text-slate-900 dark:text-white focus:ring-2 focus:ring-[#0f766e]/50 dark:focus:ring-[#D4F06B]/50 focus:border-[#0f766e] dark:focus:border-[#D4F06B] outline-none transition-all placeholder-slate-400 dark:placeholder-slate-600 resize-none font-general-regular disabled:opacity-50"
            ></textarea>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary w-full py-4 text-base mt-2 flex items-center justify-center gap-2 neon-action disabled:opacity-80"
          >
            {isSubmitting ? (
              <>
                <ClipLoader size={18} color="#000" className="dark:!hidden" />
                <ClipLoader size={18} color="#000" className="hidden dark:!block" />
                <span>Sending Application...</span>
              </>
            ) : (
              <>
                <span>Send Request</span>
                <FiSend className="w-4 h-4" />
              </>
            )}
          </button>
        </form>
      </motion.div>
    </Modal>
  );
};

export default HireMeModal;
